/**
 * Validações de documentos, endereço, datas e busca — COMPARTILHADAS pela
 * aplicação (cadastros do Comercial) e pela ferramenta de migração (Fase B / B0).
 * Porte fiel de `frontend/prototipo/src/validacao.ts` (tag demo-03-aprovada),
 * com uma diferença deliberada: a faixa de CNPJs FICTÍCIOS da DEMO-03
 * (00.000.xxx/000n-00, dígitos verificadores inválidos de propósito) só é
 * aceita quando o chamador habilita `aceitarFicticio` (teste e demonstração).
 * A lista de Origens do Cliente NÃO foi portada: é parâmetro a validar (P-09).
 */
export const soDigitos = (s: string) => s.replace(/\D/g, '');

/* ---------- CNPJ ---------- */
export const formatarCnpj = (s: string) => {
  const d = soDigitos(s).slice(0, 14);
  if (d.length !== 14) return s.trim();
  return `${d.slice(0, 2)}.${d.slice(2, 5)}.${d.slice(5, 8)}/${d.slice(8, 12)}-${d.slice(12)}`;
};
/** Dígitos verificadores do CNPJ a partir dos 12 primeiros dígitos. */
export function digitosVerificadoresCnpj(base12: string): string {
  const calc = (base: string) => {
    const pesos = base.length === 12 ? [5, 4, 3, 2, 9, 8, 7, 6, 5, 4, 3, 2] : [6, 5, 4, 3, 2, 9, 8, 7, 6, 5, 4, 3, 2];
    const soma = [...base].reduce((s, c, i) => s + Number(c) * pesos[i], 0);
    const r = soma % 11;
    return r < 2 ? 0 : 11 - r;
  };
  const d1 = calc(base12);
  return `${d1}${calc(base12 + d1)}`;
}
/** Faixa reservada da DEMO-03: 00.000.xxx/000n-00 (dígitos verificadores inválidos = inexistente). */
export const cnpjFicticioDemo = (s: string) => /^00000\d{3}000\d00$/.test(soDigitos(s));
export type ResultadoCnpj = { ok: true; formatado: string; ficticio: boolean } | { ok: false; erro: string };
export function validarCnpj(entrada: string, opcoes: { aceitarFicticio?: boolean } = {}): ResultadoCnpj {
  const d = soDigitos(entrada);
  if (!d) return { ok: false, erro: 'Informe o CNPJ.' };
  if (d.length !== 14) return { ok: false, erro: `O CNPJ precisa ter 14 dígitos (foram informados ${d.length}).` };
  if (/^(\d)\1{13}$/.test(d)) return { ok: false, erro: 'CNPJ inválido (todos os dígitos iguais).' };
  if (cnpjFicticioDemo(d)) {
    return opcoes.aceitarFicticio ? { ok: true, formatado: formatarCnpj(d), ficticio: true } : { ok: false, erro: 'CNPJ da faixa fictícia de demonstração: não é aceito neste ambiente.' };
  }
  if (digitosVerificadoresCnpj(d.slice(0, 12)) !== d.slice(12)) return { ok: false, erro: 'Dígitos verificadores do CNPJ não conferem.' };
  return { ok: true, formatado: formatarCnpj(d), ficticio: false };
}
export const mesmoCnpj = (a: string, b: string) => soDigitos(a) === soDigitos(b) && soDigitos(a).length === 14;

/* ---------- CPF (Financeiro A1: pessoa física; guardado só com dígitos, exibido mascarado) ---------- */
export const formatarCpf = (s: string) => {
  const d = soDigitos(s);
  return d.length === 11 ? `${d.slice(0, 3)}.${d.slice(3, 6)}.${d.slice(6, 9)}-${d.slice(9)}` : s.trim();
};
/** Máscara de exibição: só os 2 dígitos verificadores ficam visíveis (***.***.***-12). */
export const mascararCpf = (s: string) => {
  const d = soDigitos(s);
  return d.length === 11 ? `***.***.***-${d.slice(9)}` : '***';
};
export function validarCpf(entrada: string): { ok: true; digitos: string } | { ok: false; erro: string } {
  const d = soDigitos(entrada);
  if (!d) return { ok: false, erro: 'Informe o CPF.' };
  if (d.length !== 11) return { ok: false, erro: `O CPF precisa ter 11 dígitos (foram informados ${d.length}).` };
  if (/^(\d)\1{10}$/.test(d)) return { ok: false, erro: 'CPF inválido (todos os dígitos iguais).' };
  const dv = (base: string) => {
    const soma = [...base].reduce((t, c, i) => t + Number(c) * (base.length + 1 - i), 0);
    const r = (soma * 10) % 11;
    return r === 10 ? 0 : r;
  };
  const d1 = dv(d.slice(0, 9));
  const d2 = dv(d.slice(0, 9) + d1);
  if (`${d1}${d2}` !== d.slice(9)) return { ok: false, erro: 'Dígitos verificadores do CPF não conferem.' };
  return { ok: true, digitos: d };
}

/* ---------- CEP ---------- */
export const formatarCep = (s: string) => {
  const d = soDigitos(s).slice(0, 8);
  return d.length === 8 ? `${d.slice(0, 5)}-${d.slice(5)}` : s.trim();
};
export function validarCep(entrada: string): { ok: true; formatado: string } | { ok: false; erro: string } {
  const d = soDigitos(entrada);
  if (!d) return { ok: false, erro: 'Informe o CEP.' };
  if (d.length !== 8) return { ok: false, erro: `O CEP precisa ter 8 dígitos (foram informados ${d.length}).` };
  return { ok: true, formatado: formatarCep(d) };
}

/* ---------- UF (27 unidades federativas) ---------- */
export const UFS: { sigla: string; nome: string }[] = [
  { sigla: 'AC', nome: 'Acre' },
  { sigla: 'AL', nome: 'Alagoas' },
  { sigla: 'AP', nome: 'Amapá' },
  { sigla: 'AM', nome: 'Amazonas' },
  { sigla: 'BA', nome: 'Bahia' },
  { sigla: 'CE', nome: 'Ceará' },
  { sigla: 'DF', nome: 'Distrito Federal' },
  { sigla: 'ES', nome: 'Espírito Santo' },
  { sigla: 'GO', nome: 'Goiás' },
  { sigla: 'MA', nome: 'Maranhão' },
  { sigla: 'MT', nome: 'Mato Grosso' },
  { sigla: 'MS', nome: 'Mato Grosso do Sul' },
  { sigla: 'MG', nome: 'Minas Gerais' },
  { sigla: 'PA', nome: 'Pará' },
  { sigla: 'PB', nome: 'Paraíba' },
  { sigla: 'PR', nome: 'Paraná' },
  { sigla: 'PE', nome: 'Pernambuco' },
  { sigla: 'PI', nome: 'Piauí' },
  { sigla: 'RJ', nome: 'Rio de Janeiro' },
  { sigla: 'RN', nome: 'Rio Grande do Norte' },
  { sigla: 'RS', nome: 'Rio Grande do Sul' },
  { sigla: 'RO', nome: 'Rondônia' },
  { sigla: 'RR', nome: 'Roraima' },
  { sigla: 'SC', nome: 'Santa Catarina' },
  { sigla: 'SP', nome: 'São Paulo' },
  { sigla: 'SE', nome: 'Sergipe' },
  { sigla: 'TO', nome: 'Tocantins' },
];

/* ---------- Datas DD/MM/AAAA ---------- */
/** Máscara progressiva: só dígitos, com as barras inseridas automaticamente. */
export function mascararData(s: string) {
  const d = soDigitos(s).slice(0, 8);
  return [d.slice(0, 2), d.slice(2, 4), d.slice(4, 8)].filter(Boolean).join('/');
}
/** DD/MM/AAAA → ISO (AAAA-MM-DD), ou erro se não for uma data real. */
export function lerDataBr(s: string): { ok: true; iso: string } | { ok: false; erro: string } {
  const m = s.trim().match(/^(\d{2})\/(\d{2})\/(\d{4})$/);
  if (!m) return { ok: false, erro: 'Use o formato DD/MM/AAAA.' };
  const [dia, mes, ano] = [Number(m[1]), Number(m[2]), Number(m[3])];
  if (ano < 1900 || ano > 2100) return { ok: false, erro: 'Ano fora do intervalo aceito (1900 a 2100).' };
  if (mes < 1 || mes > 12) return { ok: false, erro: 'Mês inválido.' };
  const ultimo = new Date(Date.UTC(ano, mes, 0)).getUTCDate();
  if (dia < 1 || dia > ultimo) return { ok: false, erro: `Data inexistente: ${m[2]}/${m[3]} tem ${ultimo} dias.` };
  return { ok: true, iso: `${m[3]}-${m[2]}-${m[1]}` };
}
export const isoParaBr = (iso: string) => (iso ? `${iso.slice(8, 10)}/${iso.slice(5, 7)}/${iso.slice(0, 4)}` : '');

/* ---------- Busca de cadastro (nome, razão, CNPJ, e-mail, telefone) ---------- */
export const semAcento = (s: string) => s.normalize('NFD').replace(/[\u0300-\u036f]/g, '').toLowerCase();
/** Confere um texto de busca contra campos: texto parcial sem acento; números (CNPJ, telefone) com ou sem máscara. */
export function casaCadastro(consulta: string, campos: string[]): boolean {
  const q = semAcento(consulta.trim());
  if (!q) return true;
  const num = soDigitos(q);
  const soNumero = /^[\d\s().\/-]+$/.test(q) && num.length >= 3;
  return campos.some((c) => (soNumero ? soDigitos(c).includes(num) : semAcento(c).includes(q)));
}
